import { useEffect, useRef } from 'react';
import { Button, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { t } from 'i18next';
import { useStore } from '@/store';
import { useReevaluate } from '@/hooks';
import {
  changedIteratorMaps,
  fieldsBelow,
  iteratorSnapshot,
} from '../../../../utils/reevaluation';

/** Wait until the iterator stops changing (e.g. while typing in it). */
export const SETTLE_MS = 1500;
const STALE_NOTICE_ID = 'reevaluate-stale-fields';

const isBusy = (entryId: string) => {
  const status = useStore.getState().ai.fieldStatus[entryId]?.status;
  return status === 'queued' || status === 'running';
};

const showStaleNotice = (count: number, review: () => void) => {
  const notice = {
    id: STALE_NOTICE_ID,
    title: t('reevaluate.staleTitle'),
    autoClose: false,
    message: (
      <Stack spacing="xs" align="flex-start">
        <Text size="sm">{t('reevaluate.staleMessage', { count })}</Text>
        <Button
          size="xs"
          onClick={() => {
            notifications.hide(STALE_NOTICE_ID);
            review();
          }}
        >
          {t('reevaluate.staleAction')}
        </Button>
      </Stack>
    ),
  };
  // Replace the notice of an earlier change instead of stacking them.
  notifications.hide(STALE_NOTICE_ID);
  notifications.show(notice);
};

/**
 * Watches the iterators in the template (#1053). When one changes, the
 * fields below it are marked stale: empty fields are evaluated again right
 * away, filled fields keep their value and get a notice to review them.
 */
export const useIteratorWatcher = () => {
  const { evaluateAndFill } = useReevaluate();
  const evaluateRef = useRef(evaluateAndFill);
  evaluateRef.current = evaluateAndFill;

  useEffect(() => {
    let entries = useStore.getState().entries.entries;
    let snapshot = iteratorSnapshot(entries);
    const changedMaps = new Set<string>();
    let timer: ReturnType<typeof setTimeout> | undefined;

    const settle = () => {
      timer = undefined;
      const state = useStore.getState();
      const stale = fieldsBelow([...changedMaps], state.entries.entries);
      changedMaps.clear();
      if (stale.length === 0) return;

      state.ai.markStale(stale);
      // Fields already queued (e.g. by a running re-evaluation) are covered.
      const idle = stale.filter((id) => !isBusy(id));
      const filled = idle.filter((id) => state.entries.hasValue(id));
      const empty = idle.filter((id) => !state.entries.hasValue(id));

      if (filled.length > 0) {
        showStaleNotice(filled.length, () => state.ai.setReevaluateOpen(true));
      }
      if (empty.length > 0) void evaluateRef.current(empty);
    };

    const unsubscribe = useStore.subscribe((state) => {
      if (state.entries.entries === entries) return;
      entries = state.entries.entries;
      const next = iteratorSnapshot(entries);
      changedIteratorMaps(snapshot, next).forEach((id) => changedMaps.add(id));
      snapshot = next;

      if (changedMaps.size === 0) return;
      clearTimeout(timer);
      timer = setTimeout(settle, SETTLE_MS);
    });

    return () => {
      unsubscribe();
      clearTimeout(timer);
    };
  }, []);
};
